"use client"

import { useState, useEffect } from "react"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Check, Star, Loader2, AlertCircle, Sparkles } from "lucide-react"
import { useSession } from "@/app/context/AuthContext"
import { useToast } from "@/hooks/use-toast"
import {
  listSubscriptionPlanCatalog,
  listCreditPackCatalog,
  startCheckout,
  startCreditPackCheckout,
  getCurrentSubscription,
} from "@/src/utils/billingApi"
import { extractPrice, hasActiveSubscription, getPricingBadge, formatCurrency } from "@/src/utils/pricing-helpers"

interface SubscriptionPlan {
  id: string
  name: string
  price: number
  description: string
  features: string[]
  popular?: boolean
}

interface CreditPack {
  id: string
  credits: number
  price: number
  pricePerCredit: number
  bestValue?: boolean
  badge?: string | null
}

interface BillingModalProps {
  isOpen: boolean
  onClose: () => void
  returnUrl?: string
  onBeforePurchase?: () => void // Added callback for saving state before Stripe redirect
}

export function BillingModal({ isOpen, onClose, returnUrl, onBeforePurchase }: BillingModalProps) {
  const [activeTab, setActiveTab] = useState<"subscription" | "credits">("subscription")
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([])
  const [creditPacks, setCreditPacks] = useState<CreditPack[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [purchaseLoading, setPurchaseLoading] = useState<string | null>(null)
  const [hasSubscription, setHasSubscription] = useState(false)
  const { data: session } = useSession()
  const { toast } = useToast()

  console.log("[v0] BillingModal rendered:", {
    isOpen,
    hasCallback: !!onBeforePurchase,
    returnUrl,
  })

  useEffect(() => {
    const loadBillingData = async () => {
      try {
        setLoading(true)
        setError(null)

        if (session?.user?.id) {
          console.log("[v0] BillingModal - Checking subscription status for user:", session.user.id)
          try {
            const subResult = await getCurrentSubscription(session.user.id)
            if (subResult.success && subResult.subscription) {
              const qualifies = hasActiveSubscription(subResult.subscription)
              setHasSubscription(qualifies)
              console.log("[v0] BillingModal - User subscription status:", {
                planKey: subResult.subscription.planKey,
                status: subResult.subscription.status,
                qualifiesForMemberPricing: qualifies,
              })
            }
          } catch (error) {
            console.error("[v0] BillingModal - Error checking subscription:", error)
          }
        }

        // Load subscription plans
        const catalogResult = await listSubscriptionPlanCatalog()
        const plans: SubscriptionPlan[] = []

        if (catalogResult.plans && Array.isArray(catalogResult.plans) && catalogResult.plans.length > 0) {
          catalogResult.plans.forEach((plan) => {
            if (plan.planKey === "meter_only" || plan.planKey === "business") {
              return
            }

            const monthlyPrice = plan.recurring_amount_cents ? plan.recurring_amount_cents / 100 : 0

            plans.push({
              id: plan.planKey,
              name: plan.marketing?.displayName || plan.planKey,
              price: monthlyPrice,
              description: plan.marketing?.badgeText || `Perfect for ${plan.planKey} users`,
              features: plan.marketing?.featureBullets || [`${plan.monthlyQuota || 0} design credits/month`],
              popular: plan.planKey === "pro",
            })
          })
        }

        setSubscriptionPlans(plans)

        const mode = "auto"
        const userId = session?.user?.id

        console.log("[v0] BillingModal - Loading catalog with mode:", { mode, userId: !!userId })

        const creditPackResult = await listCreditPackCatalog({ mode, userId })
        const packs: CreditPack[] = []

        if (creditPackResult.success && creditPackResult.catalog) {
          console.log("[v0] BillingModal - Catalog loaded:", {
            itemCount: creditPackResult.catalog.length,
            requiresSubscription: creditPackResult.requiresSubscription,
          })

          creditPackResult.catalog.forEach((pack) => {
            const price = extractPrice(pack)
            const pricePerCredit = price > 0 ? price / pack.creditUnits : 0
            const badge = getPricingBadge(pack)

            packs.push({
              id: pack.packKey,
              credits: pack.creditUnits,
              price: price,
              pricePerCredit: pricePerCredit,
              bestValue: false,
              badge: badge,
            })
          })

          // Mark the best value pack (lowest price per credit)
          if (packs.length > 0) {
            const bestValuePack = packs.reduce((best, current) =>
              current.pricePerCredit > 0 && current.pricePerCredit < best.pricePerCredit ? current : best,
            )
            bestValuePack.bestValue = true
          }
        }

        setCreditPacks(packs)
      } catch (err) {
        console.error("Failed to load billing data:", err)
        setError("Failed to load pricing information. Please try again.")
      } finally {
        setLoading(false)
      }
    }

    if (isOpen) {
      loadBillingData()
    }
  }, [isOpen, session?.user?.id])

  useEffect(() => {
    if (hasSubscription) {
      setActiveTab("credits")
    }
  }, [hasSubscription])

  const handleSubscriptionPurchase = async (planId: string) => {
    console.log("[v0] handleSubscriptionPurchase called:", {
      planId,
      hasCallback: !!onBeforePurchase,
    })

    if (planId === "business") {
      toast({
        title: "Business Plan",
        description: "The Business plan is coming soon. Please check back later or contact us for more information.",
      })
      return
    }

    if (!session?.user?.id) {
      toast({
        title: "Authentication required",
        description: "Please log in to purchase a subscription.",
        variant: "destructive",
      })
      return
    }

    try {
      setPurchaseLoading(planId)

      console.log("[v0] Calling onBeforePurchase callback...")
      onBeforePurchase?.()
      console.log("[v0] onBeforePurchase callback completed")

      // Store checkout type for return handling
      sessionStorage.setItem("checkout_type", "subscription")

      // Save current wizard state if returnUrl is provided
      if (returnUrl) {
        sessionStorage.setItem("billing_return_url", returnUrl)
      }

      // Redirect to Stripe Checkout
      await startCheckout(planId as "starter" | "pro", session.user.id)

      // Timeout fallback in case redirect fails
      setTimeout(() => {
        if (purchaseLoading === planId) {
          toast({
            title: "Checkout delayed",
            description: "If you weren't redirected to checkout, please try again.",
            variant: "destructive",
          })
          setPurchaseLoading(null)
        }
      }, 3000)
    } catch (error) {
      console.error("Subscription checkout error:", error)
      toast({
        title: "Checkout failed",
        description: "Unable to start checkout process. Please try again.",
        variant: "destructive",
      })
      setPurchaseLoading(null)
    }
  }

  const handleCreditPackPurchase = async (packId: string) => {
    console.log("[v0] handleCreditPackPurchase called:", {
      packId,
      hasCallback: !!onBeforePurchase,
    })

    if (!session?.user?.id) {
      toast({
        title: "Authentication required",
        description: "Please log in to purchase credits.",
        variant: "destructive",
      })
      return
    }

    try {
      setPurchaseLoading(packId)
      const pack = creditPacks.find((p) => p.id === packId)
      const creditAmount = pack ? pack.credits : 100

      console.log("[v0] Calling onBeforePurchase callback...")
      onBeforePurchase?.()
      console.log("[v0] onBeforePurchase callback completed")

      console.log("[v0] ⏰ WAITING 5 SECONDS BEFORE REDIRECT - Copy logs now!")
      await new Promise((resolve) => setTimeout(resolve, 5000))
      console.log("[v0] ⏰ Proceeding with Stripe redirect...")

      // Store checkout type and credit amount for return handling
      sessionStorage.setItem("checkout_type", "credit_pack")
      sessionStorage.setItem("credit_amount", creditAmount.toString())

      // Save current wizard state if returnUrl is provided
      if (returnUrl) {
        sessionStorage.setItem("billing_return_url", returnUrl)
      }

      // Redirect to Stripe Checkout
      await startCreditPackCheckout(packId, 1, session.user.id, creditAmount)

      // Timeout fallback in case redirect fails
      setTimeout(() => {
        if (purchaseLoading === packId) {
          toast({
            title: "Checkout delayed",
            description: "If you weren't redirected to checkout, please try again.",
            variant: "destructive",
          })
          setPurchaseLoading(null)
        }
      }, 3000)
    } catch (error) {
      console.error("Credit pack checkout error:", error)
      toast({
        title: "Checkout failed",
        description: "Unable to start checkout process. Please try again.",
        variant: "destructive",
      })
      setPurchaseLoading(null)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 rounded-full bg-[#40A1AB]/10 flex items-center justify-center">
              <Sparkles className="h-6 w-6 text-[#40A1AB]" />
            </div>
          </div>
          <DialogTitle className="text-3xl font-bold text-center">You Need Credits to Continue</DialogTitle>
          <DialogDescription className="text-base mt-2 text-center">
            Choose a subscription for the best value with monthly credits, or purchase a one-time credit pack to get
            started right away.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#40A1AB]" />
            <span className="ml-3 text-gray-600">Loading pricing options...</span>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-12 text-red-600">
            <AlertCircle className="h-6 w-6 mr-2" />
            <span>{error}</span>
          </div>
        ) : (
          <Tabs
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as "subscription" | "credits")}
            className="mt-4"
          >
            <TabsList className="grid w-full grid-cols-2 mb-8">
              <TabsTrigger value="subscription" className="text-base">
                Monthly Subscription
              </TabsTrigger>
              <TabsTrigger value="credits" className="text-base">
                Credit Packs
              </TabsTrigger>
            </TabsList>

            <TabsContent value="subscription" className="mt-0">
              <div className="space-y-6">
                <div
                  className={
                    subscriptionPlans.length < 3
                      ? "flex flex-wrap justify-center gap-8 max-w-5xl mx-auto"
                      : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto"
                  }
                >
                  {subscriptionPlans.map((plan) => (
                    <Card
                      key={plan.id}
                      className={`relative flex flex-col ${plan.popular ? "ring-2 ring-[#40A1AB] shadow-lg" : "border-gray-200"} ${subscriptionPlans.length < 3 ? "w-80" : ""}`}
                    >
                      {plan.popular && (
                        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                          <Badge className="bg-[#40A1AB] hover:bg-[#40A1AB] text-white px-4 py-1.5 text-sm font-semibold shadow-md">
                            <Star className="h-3.5 w-3.5 mr-1 fill-white" />
                            Most Popular
                          </Badge>
                        </div>
                      )}

                      <CardHeader className="text-center pb-4 pt-8">
                        <CardTitle className="text-2xl font-bold mb-3">{plan.name}</CardTitle>
                        <div className="mb-3">
                          <div className="flex items-baseline justify-center gap-1">
                            <span className="text-4xl font-bold tracking-tight">
                              {plan.id === "business" ? "TBD" : `$${formatCurrency(plan.price)}`}
                            </span>
                            {plan.price > 0 && <span className="text-gray-500 text-lg">/month</span>}
                          </div>
                        </div>
                        <CardDescription className="text-sm text-gray-600">{plan.description}</CardDescription>
                      </CardHeader>

                      <CardContent className="flex-grow pt-2">
                        <ul className="space-y-3">
                          {plan.features.map((feature, index) => (
                            <li key={index} className="flex items-start">
                              <Check className="h-5 w-5 text-[#40A1AB] mr-3 flex-shrink-0 mt-0.5" />
                              <span className="text-sm text-gray-700">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>

                      <CardFooter className="pt-6">
                        <Button
                          onClick={() => handleSubscriptionPurchase(plan.id)}
                          className={`w-full h-12 text-base font-semibold ${
                            plan.popular
                              ? "bg-[#40A1AB] hover:bg-[#3A919A] text-white shadow-md"
                              : "bg-white hover:bg-gray-50 text-gray-900 border-2 border-gray-300"
                          }`}
                          disabled={purchaseLoading === plan.id || hasSubscription}
                        >
                          {purchaseLoading === plan.id ? (
                            <>
                              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                              Processing...
                            </>
                          ) : hasSubscription ? (
                            "Currently Subscribed"
                          ) : plan.id === "business" ? (
                            "Join the Waitlist"
                          ) : (
                            "Subscribe Now"
                          )}
                        </Button>
                      </CardFooter>
                    </Card>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="credits" className="mt-0">
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <h3 className="text-xl font-semibold mb-2">Credit Packs</h3>
                  <p className="text-gray-600">Purchase credits as needed for one-time projects</p>
                </div>

                <div
                  className={
                    creditPacks.length < 3
                      ? "flex flex-wrap justify-center gap-6 max-w-5xl mx-auto"
                      : "grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto"
                  }
                >
                  {creditPacks.map((pack) => (
                    <Card
                      key={pack.id}
                      className={`relative flex flex-col ${pack.bestValue ? "ring-2 ring-[#40A1AB] shadow-lg" : "border-gray-200"}`}
                    >
                      {pack.bestValue && (
                        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                          <Badge className="bg-[#40A1AB] hover:bg-[#40A1AB] text-white px-4 py-1.5 text-sm font-semibold shadow-md">
                            Best Value
                          </Badge>
                        </div>
                      )}

                      <CardHeader className="text-center pb-4 pt-8">
                        <CardTitle className="text-2xl font-bold mb-3">{pack.credits} Credits</CardTitle>
                        <div className="mb-2">
                          <span className="text-4xl font-bold tracking-tight">
                            {pack.price > 0 ? `$${formatCurrency(pack.price)}` : "Contact for pricing"}
                          </span>
                          {pack.badge && (
                            <div className="mt-2">
                              <Badge variant="secondary" className="text-xs">
                                {pack.badge}
                              </Badge>
                            </div>
                          )}
                        </div>
                        <CardDescription className="text-sm text-gray-600">
                          {pack.price > 0
                            ? `$${pack.pricePerCredit.toFixed(2)} per credit`
                            : "Pricing available upon request"}
                        </CardDescription>
                        {!hasSubscription && (
                          <div className="mt-3 flex items-center justify-center gap-1.5 text-sm text-muted-foreground">
                            <span className="text-base">💡</span>
                            <span>Members save on credits</span>
                          </div>
                        )}
                      </CardHeader>

                      <CardContent className="flex-grow pt-2">
                        <ul className="space-y-3">
                          <li className="flex items-start">
                            <Check className="h-5 w-5 text-[#40A1AB] mr-3 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-gray-700">One-time purchase</span>
                          </li>
                          <li className="flex items-start">
                            <Check className="h-5 w-5 text-[#40A1AB] mr-3 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-gray-700">Credits never expire</span>
                          </li>
                          <li className="flex items-start">
                            <Check className="h-5 w-5 text-[#40A1AB] mr-3 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-gray-700">Use at your own pace</span>
                          </li>
                        </ul>
                      </CardContent>

                      <CardFooter className="pt-6">
                        <Button
                          onClick={() => handleCreditPackPurchase(pack.id)}
                          className={`w-full h-12 text-base font-semibold ${
                            pack.bestValue
                              ? "bg-[#40A1AB] hover:bg-[#3A919A] text-white shadow-md"
                              : "bg-white hover:bg-gray-50 text-gray-900 border-2 border-gray-300"
                          }`}
                          disabled={purchaseLoading === pack.id}
                        >
                          {purchaseLoading === pack.id ? (
                            <>
                              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                              Processing...
                            </>
                          ) : (
                            "Buy Now"
                          )}
                        </Button>
                      </CardFooter>
                    </Card>
                  ))}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  )
}
